import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useStavby } from '../../api/hooks';
import type { Stavba } from '../../api/schemas';

/**
 * Globální kontext "aktivní stavby" — většina entitních endpointů je scoped
 * pod /api/stavby/{stavba_id}/..., potřebujeme jeden zdroj pravdy, která je aktivní.
 * Persistence v localStorage, při prvním načtení fallback na první dostupnou stavbu.
 */

interface StavbaContextValue {
  activeStavba: Stavba | null;
  activeStavbaId: number | null;
  stavby: Stavba[];
  isLoading: boolean;
  setActiveStavbaId: (id: number | null) => void;
}

const StavbaContext = createContext<StavbaContextValue | null>(null);

const STORAGE_KEY = 'sd_active_stavba_id';
/** Sentinel pro explicitní volbu „žádná stavba" (práce mimo stavbu — účetní, zkoušení). */
const NONE = 'none';

/**
 * Uložená volba: konkrétní id, sentinel NONE (uživatel vědomě zvolil „žádná
 * stavba"), nebo null (zatím nevybráno → smí proběhnout auto-výběr).
 */
type Selection = number | typeof NONE | null;

function loadStored(): Selection {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    if (raw === NONE) return NONE;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function StavbaProvider({ children }: { children: ReactNode }) {
  const { data: stavby = [], isLoading } = useStavby();
  const [selection, setSelection] = useState<Selection>(() => loadStored());

  const setActiveStavbaId = useCallback((id: number | null) => {
    const next: Selection = id === null ? NONE : id;
    setSelection(next);
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      /* ignore */
    }
  }, []);

  // Auto-výběr jen když uživatel ještě nic nezvolil (selection === null).
  // Explicitní „žádná stavba" (NONE) i platná uložená stavba zůstávají.
  // Neexistující uložené id (smazaná stavba) → fallback na první dostupnou.
  useEffect(() => {
    if (stavby.length === 0) return;
    if (selection === null) {
      setActiveStavbaId(stavby[0].id);
    } else if (typeof selection === 'number' && !stavby.some((s) => s.id === selection)) {
      setActiveStavbaId(stavby[0].id);
    }
  }, [stavby, selection, setActiveStavbaId]);

  const activeStavba = useMemo(
    () =>
      typeof selection === 'number'
        ? stavby.find((s) => s.id === selection) ?? null
        : null,
    [stavby, selection],
  );

  const value = useMemo<StavbaContextValue>(
    () => ({
      activeStavba,
      activeStavbaId: activeStavba?.id ?? null,
      stavby,
      isLoading,
      setActiveStavbaId,
    }),
    [activeStavba, stavby, isLoading, setActiveStavbaId],
  );

  return <StavbaContext.Provider value={value}>{children}</StavbaContext.Provider>;
}

export function useActiveStavba(): StavbaContextValue {
  const ctx = useContext(StavbaContext);
  if (!ctx) throw new Error('useActiveStavba must be used within StavbaProvider');
  return ctx;
}
